// Auto-reload for the staff screens when a new version is deployed.
//
// Why: the app is a PWA with `registerType: 'autoUpdate'`. The new service
// worker takes control quickly, but an OPEN tab keeps running the old
// JavaScript until it reloads. On 2026-09-23 that cost a full afternoon of
// "the new button doesn't appear" / "the function is never called" — the
// deployed code was fine, the staff tab was stale.
//
// What this does, on /staff and /admin routes only:
//   • asks the service worker to check for a new version every 5 minutes
//   • when a new version actually takes control, reloads the page
//
// What it never does:
//   • touch the customer side (booking). A reload mid-order would lose the
//     form, so the customer app keeps the normal behaviour: new version on
//     the next navigation.
//   • reload while work is in progress. If a ticket has unsaved edits
//     (window.__ghostUnsaved, set by OrderPanel) or the tab is being typed
//     in, we wait: the reload happens as soon as the tab goes to the
//     background, or after 90 s without any interaction.

const CHECK_EVERY_MS = 5 * 60 * 1000
const IDLE_MS = 90 * 1000

const isStaffRoute = () => /^\/(staff|admin)/.test(window.location.pathname)

export function installStaffAutoReload() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return

  // A first install also fires controllerchange; only an UPDATE should
  // reload, so remember whether a controller was already in charge.
  const hadController = !!navigator.serviceWorker.controller
  let reloading = false
  let pending = false
  let lastInteraction = Date.now()

  const markInteraction = () => { lastInteraction = Date.now() }
  for (const evt of ['keydown', 'pointerdown', 'input']) {
    window.addEventListener(evt, markInteraction, { passive: true })
  }

  const doReload = () => {
    if (reloading) return
    reloading = true
    window.location.reload()
  }

  const tryReload = () => {
    if (!pending || reloading) return
    if (!isStaffRoute()) return            // customer side: never auto-reload
    if (document.visibilityState === 'hidden') return doReload()
    if (window.__ghostUnsaved) return       // a ticket has unsaved edits
    if (Date.now() - lastInteraction < IDLE_MS) return
    doReload()
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return              // first install, not an update
    pending = true
    tryReload()
  })

  document.addEventListener('visibilitychange', tryReload)
  setInterval(tryReload, 15 * 1000)

  // Ask the browser to look for a new version regularly. Without this, a
  // staff tab left open all day may never notice a deploy.
  navigator.serviceWorker.getRegistration().then((reg) => {
    if (!reg) return
    const check = () => { if (isStaffRoute()) reg.update().catch(() => {}) }
    setInterval(check, CHECK_EVERY_MS)
    check()
  }).catch(() => {})
}
